/**
 * Utility functions for cleaning up localStorage
 */

export function cleanupLocalStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    // Clean up old session data (older than 7 days)
    const now = Date.now();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

    const keysToCheck = Object.keys(localStorage);
    
    keysToCheck.forEach(key => {
      // Check for timestamp-based keys
      if (key.includes('_timestamp')) {
        const timestamp = parseInt(localStorage.getItem(key) || '0', 10);
        if (timestamp && now - timestamp > sevenDaysInMs) {
          // Remove the timestamp key and its associated data key
          localStorage.removeItem(key);
          const dataKey = key.replace('_timestamp', '');
          localStorage.removeItem(dataKey);
        }
      }
      
      // Clean up temporary drawing data
      if (key.startsWith('temp_drawing_') || key.startsWith('temp_stroke_')) {
        localStorage.removeItem(key);
      }
      
      // Clean up old question data (if test is not active)
      if (key.startsWith('questionData_') && key.includes('_old')) {
        localStorage.removeItem(key);
      }
    });

    // Check total localStorage usage and clean if needed
    const totalSize = new Blob(Object.values(localStorage)).size;
    const maxSize = 5 * 1024 * 1024; // 5MB threshold
    
    if (totalSize > maxSize) {
      // Remove oldest entries first
      const entries = keysToCheck
        .filter(key => key.includes('_timestamp'))
        .map(key => ({
          key: key.replace('_timestamp', ''),
          timestamp: parseInt(localStorage.getItem(key) || '0', 10)
        }))
        .filter(entry => entry.timestamp > 0)
        .sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 20% of entries
      const entriesToRemove = Math.floor(entries.length * 0.2);
      for (let i = 0; i < entriesToRemove; i++) {
        localStorage.removeItem(entries[i].key);
        localStorage.removeItem(entries[i].key + '_timestamp');
      }
    }
  } catch (error) {
    console.error('Error cleaning up localStorage:', error);
  }
}

/**
 * Clean up data for a specific test
 */
export function cleanupTestData(testId: string): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.includes(testId) && 
      (key.startsWith('questionData_') || 
       key.startsWith('testSession_') ||
       key.startsWith('recording_'))
    );
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error cleaning up test data:', error);
  }
}

/**
 * Clean up recording-specific localStorage entries
 */
export function cleanupRecordingLocalStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const now = Date.now();
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    
    const keysToCheck = Object.keys(localStorage).filter(key => 
      key.startsWith('recording_') || key.startsWith('audio_')
    );
    
    keysToCheck.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (parsed.timestamp && now - parsed.timestamp > thirtyDaysInMs) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        // If can't parse, remove it
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error cleaning up recording localStorage:', error);
  }
}

/**
 * Get the size of localStorage in bytes
 */
export function getLocalStorageSize(): number {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 0;
  }

  try {
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        const value = localStorage.getItem(key) || '';
        // Rough estimate: each character is 2 bytes in UTF-16
        totalSize += (key.length + value.length) * 2;
      }
    }
    return totalSize;
  } catch (error) {
    console.error('Error calculating localStorage size:', error);
    return 0;
  }
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}